/**
 * Saved workspace layouts — named snapshots of tabs + sidebar + panel state.
 *
 * Each layout stores: open tabs, sidebar collapse state, agent panel open/width.
 * Saved per workspace ID.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useAgentStore } from "./agent";
import { useSidebarStore } from "./sidebar";
import { useTabStore, type GlobalTab } from "./tabs";

export interface SavedLayout {
	id: string;
	name: string;
	createdAt: number;
	/** Snapshot data. */
	tabs: GlobalTab[];
	activeTabId: string | null;
	sidebarCollapsed: boolean;
	agentOpen: boolean;
	agentWidth: number;
}

interface LayoutState {
	/** Map of workspace_id → saved layouts. */
	layouts: Record<string, SavedLayout[]>;

	/** Save current state as a named layout. */
	saveLayout: (workspaceId: string, name: string) => void;
	/** Restore a saved layout. */
	restoreLayout: (workspaceId: string, layoutId: string) => void;
	/** Delete a saved layout. */
	deleteLayout: (workspaceId: string, layoutId: string) => void;
	/** Rename a saved layout. */
	renameLayout: (workspaceId: string, layoutId: string, name: string) => void;
	/** Get layouts for a workspace. */
	getLayouts: (workspaceId: string) => SavedLayout[];
}

export const useLayoutStore = create<LayoutState>()(
	persist(
		(set, get) => ({
			layouts: {},

			saveLayout: (workspaceId, name) => {
				const tabState = useTabStore.getState();
				const sidebar = useSidebarStore.getState();
				const agent = useAgentStore.getState();

				const layout: SavedLayout = {
					id: `layout-${Date.now()}`,
					name,
					createdAt: Date.now(),
					tabs: tabState.tabs.map(({ dirty, ...rest }) => rest),
					activeTabId: tabState.activeTabId,
					sidebarCollapsed: sidebar.isCollapsed,
					agentOpen: agent.isOpen,
					agentWidth: agent.width,
				};

				set((s) => ({
					layouts: {
						...s.layouts,
						[workspaceId]: [...(s.layouts[workspaceId] ?? []), layout],
					},
				}));
			},

			restoreLayout: (workspaceId, layoutId) => {
				const layout = get().layouts[workspaceId]?.find(
					(l) => l.id === layoutId,
				);
				if (!layout) return;

				// Restore tabs
				useTabStore.setState({
					tabs: layout.tabs,
					activeTabId: layout.activeTabId,
				});

				// Restore sidebar
				useSidebarStore.setState({
					isCollapsed: layout.sidebarCollapsed,
				});

				// Restore agent panel
				useAgentStore.setState({
					isOpen: layout.agentOpen,
					width: layout.agentWidth,
				});
			},

			deleteLayout: (workspaceId, layoutId) => {
				set((s) => ({
					layouts: {
						...s.layouts,
						[workspaceId]: (s.layouts[workspaceId] ?? []).filter(
							(l) => l.id !== layoutId,
						),
					},
				}));
			},

			renameLayout: (workspaceId, layoutId, name) => {
				set((s) => ({
					layouts: {
						...s.layouts,
						[workspaceId]: (s.layouts[workspaceId] ?? []).map((l) =>
							l.id === layoutId ? { ...l, name } : l,
						),
					},
				}));
			},

			getLayouts: (workspaceId) => {
				return get().layouts[workspaceId] ?? [];
			},
		}),
		{ name: "omnios-workspace-layouts" },
	),
);
